import axios from 'axios';

const API_URL = `${process.env.REACT_APP_SURVEY_API_URL}/api`;

// Fallback mientras la API no ha respondido aún
const FALLBACK_CATEGORIES = [
  { slug: 'matriculas', role_access: { administrador: { view: true, fill: true  }, profesor: { view: true,  fill: false } } },
  { slug: 'salud',      role_access: { administrador: { view: true, fill: true  }, profesor: { view: true,  fill: false } } },
  { slug: 'atencion',   role_access: { administrador: { view: true, fill: true  }, profesor: { view: true,  fill: true  } } },
  { slug: 'documentos', role_access: { administrador: { view: true, fill: true  }, profesor: { view: true,  fill: false } } },
  { slug: 'becas',      role_access: { administrador: { view: true, fill: true  }, profesor: { view: false, fill: false } } },
  { slug: 'casos',      role_access: { administrador: { view: true, fill: false }, profesor: { view: false, fill: false } } },
  { slug: 'otros',      role_access: { administrador: { view: true, fill: true  }, profesor: { view: true,  fill: false } } },
];

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

let _cache     = null;
let _cacheTime = null;

// Promesa en vuelo — evita que llamadas concurrentes (varios componentes
// montando a la vez, antes de que exista cache) disparen múltiples GET.
let _inFlight = null;

const apiGet = async (url) => {
  const token = localStorage.getItem('token');
  const res = await axios.get(`${API_URL}${url}`, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  return res.data;
};

/**
 * Carga las categorías desde la API y las guarda en caché.
 * Llamar esto al inicio de la sesión o después de modificar categorías.
 */
export const loadCategories = async (forceRefresh = false) => {
  const now = Date.now();
  if (!forceRefresh && _cache && _cacheTime && (now - _cacheTime < CACHE_DURATION)) {
    return _cache;
  }

  if (!forceRefresh && _inFlight) {
    return _inFlight;
  }

  _inFlight = (async () => {
    try {
      const data = await apiGet('/survey-categories');
      _cache     = Array.isArray(data) ? data : FALLBACK_CATEGORIES;
      _cacheTime = Date.now();
    } catch {
      if (!_cache) _cache = FALLBACK_CATEGORIES;
    } finally {
      _inFlight = null;
    }
    return _cache;
  })();

  return _inFlight;
};

/** Acceso síncrono — usa caché o fallback. Para usar en renders y filtros. */
export const getCachedCategories = () => _cache || FALLBACK_CATEGORIES;

/** Invalida el caché. Llamar después de crear/editar/eliminar una categoría. */
export const clearCategoriesCache = () => {
  _cache     = null;
  _cacheTime = null;
};

/**
 * Verifica si un rol puede VER una categoría.
 * Equivalente dinámico de canUserAccessCategory() en site.js.
 */
export const canView = (role, categorySlug) => {
  if (!role || !categorySlug) return false;
  const slug = categorySlug.toLowerCase();
  const cat  = getCachedCategories().find(c => (c.slug || '').toLowerCase() === slug);
  if (!cat) return role === 'administrador'; // categoría desconocida: solo admin
  return !!(cat.role_access?.[role]?.view);
};

/**
 * Verifica si un rol puede COMPLETAR formularios de una categoría.
 * Equivalente dinámico de rolesCategoriasPermitidas en surveyService.js.
 */
export const canFill = (role, categorySlug) => {
  if (!role || !categorySlug) return false;
  if (role === 'administrador') return true; // admin siempre puede completar
  const slug = categorySlug.toLowerCase();
  const cat  = getCachedCategories().find(c => (c.slug || '').toLowerCase() === slug);
  if (!cat) return false;
  return !!(cat.role_access?.[role]?.fill);
};

/** Retorna solo las categorías que el rol puede ver, ordenadas por sort_order. */
export const getCategoriesForRole = (role, type = 'view') => {
  const check = type === 'fill' ? canFill : canView;
  return getCachedCategories()
    .filter(c => check(role, c.slug))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
};

/** Retorna el label legible de una categoría desde la DB. Fallback: el slug. */
export const getLabelForCategory = (slug) => {
  if (!slug) return '';
  const cat = getCachedCategories().find(c => (c.slug || '').toLowerCase() === slug.toLowerCase());
  return cat?.label || slug;
};

/** Retorna el color Chakra de una categoría desde la DB. Fallback: 'gray'. */
export const getColorForCategory = (slug) => {
  if (!slug) return 'gray';
  const cat = getCachedCategories().find(c => (c.slug || '').toLowerCase() === slug.toLowerCase());
  return cat?.color || 'gray';
};
